/**
 * LTL "ship with" — what the scheduling save buttons share when the load
 * being saved is linked (docs/plans/2026-09-21-ltl-ship-with.md §6.2).
 *
 * The lambda fans the appointment out to every sibling from its own read of
 * the group (AD-3); the client never sends sibling ids. What the client does
 * is say so before the save, naming the loads that move along, and on the
 * customer path route the appointment through the schedule-only writer first
 * (AD-6). Both need the same question answered without side effects: does
 * this save move the appointment?
 */

import { toComparableScheduledDateTime } from "./dateUtils";
import { shipWithSiblingIds } from "./shipWith";

/**
 * Mirrors buildOutboundTimeslotOp's decision (OutboundUtils) without its
 * mutations: true when the save would create, move or clear the appointment.
 *
 * @param {object} record - the shipment as loaded
 * @param {*} scheduledDateTime - the form's value: a Date, an ISO string or empty
 * @param {object|undefined} selectedTimeslot - the scheduler's pick; undefined
 *   when the scheduler was never opened
 */
export const hasAppointmentChanged = (
  record,
  scheduledDateTime,
  selectedTimeslot,
) => {
  if (record?.timeslotId) {
    // A booked load: cleared, or moved to another instant.
    if (!scheduledDateTime) return true;
    return (
      toComparableScheduledDateTime(record.scheduledDateTime) !==
      toComparableScheduledDateTime(scheduledDateTime)
    );
  }
  // An unbooked load is booked by any pick at all.
  return Boolean(selectedTimeslot);
};

/**
 * The confirm line: "This appointment also moves 2 linked loads: PO 456, 789."
 *
 * The count comes from the record's own id list, so it stands even while the
 * members are still loading; the PO list names only the siblings that loaded,
 * never the record itself. Empty for a singleton.
 */
export const linkedAppointmentMessage = (record, members = []) => {
  const siblingIds = shipWithSiblingIds(record);
  const count = siblingIds.length;
  if (!count) return "";

  const poNumbers = (Array.isArray(members) ? members : [])
    .filter(
      (m) => m && !m.unavailable && m.poNumber && siblingIds.includes(m.id),
    )
    .map((m) => m.poNumber);

  const head = `This appointment also moves ${count} linked ${
    count === 1 ? "load" : "loads"
  }`;
  return poNumbers.length ? `${head}: PO ${poNumbers.join(", ")}.` : `${head}.`;
};
